import { Alert, Loader, tokens, Text } from '@hotjar/hotsauce';
import { SurveysPendingInstallationEmptyState } from '@hotjar/webapp/features/Activation/EmptyStates';
import { useTrackingCode } from '@hotjar/webapp/hooks/useTrackingCode';
import { useCurrentSite } from '@hotjar/site';
import { checkSkippedInstallation } from '@hotjar/webapp/features/Surveys/domain/userSettings';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useCurrentUser } from '@hotjar/user';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useSurveyQuestions } from '@hotjar/feedback/hooks/survey/useSurveyQuestions';
import { useAccountExperiment } from '@hotjar/webapp/hooks/useExperiment';
import {
  FeatureEmptyState,
  EmptyStateKind,
} from '@hotjar/webapp/features/Activation/FeatureEmptyState';
import { SurveyResponseFilters } from '../Responses/Filters/SurveyResponseFilters';
import { SurveyEmptyState } from '../EmptyState/SurveyEmptyState';
import { SIDE_BAR_WIDTH } from '../../common/SideBar/SideBar';
import { useSurveyWidgetQuery } from '../../../domain/Surveys/query';
import { TabsNav } from '../TabsNav';
import { SurveyDetailsHeader } from '../Responses/DetailsHeader';
import { LimitAlerts, useSurveyWidgetsQuery } from '../../..';
import type { ViewType } from '../Spaces/SurveySideBar';
import { SurveySideBar } from '../Spaces/SurveySideBar';
import { ResponseAPIGuideCard } from '../ResponsesAPI/GuideCard/ResponsesAPIGuideCard';

const Container = styled.div`
  display: flex;
`;

const AlertContainer = styled.div`
  margin: 0 ${tokens.spacer.x5};
`;

const FilterContainer = styled.div`
  margin: 0 ${tokens.spacer.x5} ${tokens.spacer.x2} ${tokens.spacer.x5};
`;

const ContentContainer = styled.div<{ withSideBar: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ withSideBar }) => (withSideBar ? `calc(100% - ${SIDE_BAR_WIDTH})` : '100%')};
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
`;

const ChildrenContainer = styled.div`
  margin: 0 ${tokens.spacer.x5};
`;

const EmptyStateWrapper = styled.div`
  background-color: ${tokens.color.neutral_100};
  padding: ${tokens.spacer.x4} ${tokens.spacer.x4};
  margin-bottom: ${tokens.spacer.x3};
`;

const HeaderWrapper = styled.div`
  padding: 0 ${tokens.spacer.x4};
  margin-bottom: ${tokens.spacer.x1};
`;

export interface SurveyViewProps {
  children: React.ReactNode;
  view: Exclude<ViewType, 'survey-list'>;
  surveyId: number | undefined;
  isLoaded: boolean;
}

export const SurveyView = ({
  children,
  view,
  surveyId,
  isLoaded,
}: SurveyViewProps): JSX.Element => {
  const { data: site } = useCurrentSite();
  const { data: user } = useCurrentUser();
  const { data: surveyWidgets } = useSurveyWidgetsQuery(site?.id);
  const { data: survey } = useSurveyWidgetQuery(site?.id, surveyId);
  const { hasAnyKeywords, hasAnyRatings, firstRatingStyle } = useSurveyQuestions(surveyId);
  const isExternalLinkSurvey = survey?.display_type === 'external_link';

  const { t } = useTranslation('feedback');

  const trackingCode = useTrackingCode().getState(site?.lastData);
  const [showInstallation, setShowInstallation] = useState<boolean | undefined>(undefined);
  const [skippedInstallation, setSkippedInstallation] = useState<boolean | undefined>(undefined);

  const { inVariant1: inEmptyStatesExperiment } = useAccountExperiment(
    'atv_empty_states_2023_03',
    site?.account,
  );

  const shouldRenderSideBar = !(
    inEmptyStatesExperiment &&
    (!trackingCode.installed || surveyWidgets?.length === 0)
  );

  useLayoutEffect(() => {
    if (showInstallation !== undefined) return;
    setShowInstallation(
      skippedInstallation === false &&
        survey &&
        survey.response_count === 0 &&
        !isExternalLinkSurvey &&
        !trackingCode.installed
        ? true
        : undefined,
    );
  }, [showInstallation, survey, trackingCode, skippedInstallation, isExternalLinkSurvey]);

  useEffect(() => {
    if (!user || !site) return;
    checkSkippedInstallation(user.id, site.id).then(setSkippedInstallation);
  }, [user, site]);

  const upsellLocation = useMemo(() => {
    // eslint-disable-next-line default-case
    switch (view) {
      case 'performance':
        return 'survey-performance';
      case 'responses':
        return 'survey-responses';
      case 'results':
        return 'survey-results';
    }
  }, [view]);

  return (
    <Container>
      {shouldRenderSideBar ? <SurveySideBar view={view} selectedWidget={survey} /> : null}

      {!isLoaded && (
        <LoaderContainer>
          <Loader size="xl" />
        </LoaderContainer>
      )}
      {!survey && isLoaded && (
        <ContentContainer withSideBar={shouldRenderSideBar}>
          {inEmptyStatesExperiment && !trackingCode.installed ? (
            <>
              <EmptyStateWrapper>
                <FeatureEmptyState kind={EmptyStateKind.SURVEYS} />
              </EmptyStateWrapper>
              <HeaderWrapper>
                <Text textType="header5" as="h2">
                  {t('empty-states.not-found.subtitle-new')}
                </Text>
              </HeaderWrapper>
            </>
          ) : (
            <SurveyDetailsHeader hideSharingModule={inEmptyStatesExperiment} widgetId={surveyId} />
          )}

          {surveyId !== undefined && (
            <AlertContainer>
              <Alert kind="info" title={t('empty-states.not-found.title')}>
                {t('empty-states.not-found.content')}
              </Alert>
            </AlertContainer>
          )}

          {surveyWidgets?.length === 0 && (
            <>
              <LimitAlerts
                location={upsellLocation}
                featureGroup="surveys"
                skippedInstallation={skippedInstallation}
                hidePricingAlert
              />
              <SurveyEmptyState />
            </>
          )}
        </ContentContainer>
      )}

      {survey !== undefined && isLoaded && (
        <ContentContainer withSideBar={shouldRenderSideBar}>
          <SurveyDetailsHeader widgetId={survey.id} />

          <ResponseAPIGuideCard />

          <LimitAlerts
            location={upsellLocation}
            featureGroup="surveys"
            skippedInstallation={skippedInstallation}
          />

          {showInstallation !== true && (
            <>
              <FilterContainer>
                <SurveyResponseFilters
                  hasKeywords={hasAnyKeywords}
                  hasRatings={hasAnyRatings}
                  reactionStyle={firstRatingStyle}
                  isExternalLinkSurvey={isExternalLinkSurvey}
                />
              </FilterContainer>

              <TabsNav siteId={site?.id} surveyId={surveyId} selectedValue={view} />

              <ChildrenContainer>{children}</ChildrenContainer>
            </>
          )}

          {showInstallation === true && (
            <SurveysPendingInstallationEmptyState onSkip={() => setShowInstallation(false)} />
          )}
        </ContentContainer>
      )}
    </Container>
  );
};
